import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Button,
  Card,
  Empty,
  Input,
  message,
  notification,
  Space,
  Switch,
  Tooltip,
  Typography
} from "antd";
import type { InputRef } from "antd";
import { Link } from "react-router-dom";
import {
  BranchesOutlined,
  DownOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LinkOutlined,
  RightOutlined
} from "@ant-design/icons";
import { gql } from "@apollo/client";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { BID_PACKAGE_LIST_QUERY_MIN } from "pages/planning-workspace/queries/planning-workspace-queries";
import { UPDATE_FEATURE_INSTANCE_BY_PK } from "entity-app/graphQL/ciq-feature-mutations";
import {
  MUTATION_APPLY_LINKING_CHANGES,
  MUTATION_SPLIT_DESIGN_PACKAGE
} from "services/graphQL/mutations";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { DateUtils } from "utils/dateutils";
import { PLANNER_SIDE_MARGIN } from "pages/design-package-details-page/components/view-dependencies/date-scale";
import { computeGeometry } from "pages/design-package-details-page/components/view-dependencies/layout";
import { DEFAULT_RENDER_CONFIG } from "pages/design-package-details-page/components/view-dependencies/render-config";
import DependencyTimeline from "pages/design-package-details-page/components/view-dependencies/DependencyTimeline";
import {
  DependencyModel,
  Geometry
} from "pages/design-package-details-page/components/view-dependencies/model";
import { ScopeListModal } from "pages/design-scope-page/shared/scope-list-modal";
import type { ScopeContext } from "pages/design-scope-page/shared/scope-context";
import { useDesignScopeList } from "pages/design-scope-page/hooks/use-design-scope-list";
import {
  groupScopePreviewsByBid,
  groupScopePreviewsByMilestone
} from "pages/design-scope-page/shared/design-scope-list-previews";
import type { DesignScopeListItem } from "mock-data/design-scope";
import { toModalScopeItem } from "pages/design-scope-page/shared/design-scope-list-mappers";
import { toBulkLinkDesignPackageUpdateInstance } from "pages/design-scope-page/shared/design-scope-create-mappers";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { linkBidPackages } from "services/bid-package-links";
import { useDesignPackageDetail } from "../../hooks/use-workspace-data";
import ManageBidPackagesModal from "../manage-bid-packages";
import SplitDesignPackageModal, {
  type SplitResult
} from "./split-design-package-modal";
import { splitDesignPackageNotification } from "./split-design-package-notification";
import AddDesignPackageMilestoneModal from "./add-design-package-milestone-modal";
import RelinkMilestoneModal from "./relink-milestone-modal";
import { PlannerPortalContext } from "./planner-portal";
import { useTimelineViewPref } from "./timeline-view-prefs";
import { WorkspaceModel, WorkspaceDesignPackage } from "../../model";
import {
  toDesignPackageMilestoneUpdate,
  type AddMilestonePlacement
} from "../../design-package-milestone-update";
import { bidPackageLabel } from "pages/planning-workspace/bid-package-label";
import { bidPackageDetailsPath } from "pages/planning-workspace/bid-package-path";

// UPDATE_FEATURE_INSTANCE_BY_PK is authored as a raw GraphQL string; Apollo's
// useMutation requires a parsed DocumentNode, so parse it once here — the same
// gql() wrap the mutation's other callers use. (MUTATION_APPLY_LINKING_CHANGES
// is already a gql document, so it needs no wrap.) It patches any feature
// instance by id, so it backs both the bid-package and design-package renames.
const UPDATE_FEATURE_INSTANCE = gql(UPDATE_FEATURE_INSTANCE_BY_PK);
const UPDATE_DESIGN_PACKAGE_MILESTONES = gql`
  mutation UpdateDesignPackageMilestones(
    $feature_instance_id: uuid!
    $milestones: [design_package_milestone_input!]!
    $deleted_milestone_ids: [uuid!]
  ) {
    update_design_package_milestones(
      feature_instance_id: $feature_instance_id
      milestones: $milestones
      deleted_milestone_ids: $deleted_milestone_ids
    ) {
      message
      feature_instance_id
      milestones {
        id
        name
        sequence_no
      }
    }
  }
`;

// A lane binds its log row only once it has been scrolled near the viewport,
// so a project with dozens of design packages does not mount every timeline
// body on first paint.
function useInViewport(
  forceInView = false
): [(node: HTMLElement | null) => void, boolean] {
  const [inView, setInView] = useState(forceInView);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setNode = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      if (!node || forceInView) return;

      // happy-dom supplies an IntersectionObserver constructor but its callbacks
      // never fire, so in the test environment the observer is created but silent:
      // lane headers always render, DependencyTimeline data is never fetched. This
      // branch is only reached in environments where IntersectionObserver is
      // entirely absent (e.g. jsdom without a polyfill).
      if (typeof IntersectionObserver === "undefined") {
        setInView(true);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setInView(true);
            observer.disconnect();
          }
        },
        // Pre-load a viewport ahead so a lane's timeline is ready as it scrolls in.
        { rootMargin: "200px 0px" }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [forceInView]
  );

  return [setNode, forceInView || inView];
}

async function moveDependentsToMilestone(input: {
  moveTo: string;
  milestoneId: string;
  named: { id: string }[];
  bidPackages: DependencyModel["bidPackages"];
  scopeListItems: DesignScopeListItem[];
  designPackageId: string;
  token: string | null;
  applyLinking: (
    options: {
      variables: {
        input: {
          inserts: {
            source_instance_id: string;
            source_instance_milestone_id: string;
            target_instance_id: string;
            link_type: number;
            coupling_type: number;
          }[];
          deletes: never[];
          updates: never[];
        };
        metadata: { request_source: string };
      };
    },
    flags: { showError: boolean }
  ) => Promise<{ success?: boolean; errorMsg?: string }>;
}): Promise<boolean> {
  const movedLinks = await moveBidPackageLinks(input);
  if (!movedLinks) return false;
  return moveScopeItems(input);
}

async function moveBidPackageLinks(
  input: Parameters<typeof moveDependentsToMilestone>[0]
): Promise<boolean> {
  const remaining = input.named.filter((m) => m.id !== input.milestoneId);
  const remainingLastId = remaining[remaining.length - 1]?.id;
  const linkedBps = input.bidPackages.filter(
    (bp) => bp.linkedDpMilestone?.id === input.milestoneId
  );
  if (!linkedBps.length) return true;
  const linkRes = await input.applyLinking(
    {
      variables: {
        input: {
          inserts: linkedBps.map((bp) => ({
            source_instance_id: input.designPackageId,
            source_instance_milestone_id: input.moveTo,
            target_instance_id: bp.id,
            link_type: input.moveTo === remainingLastId ? 0 : 4,
            coupling_type: 2
          })),
          deletes: [],
          updates: []
        },
        metadata: { request_source: "PLANNER" }
      }
    },
    { showError: true }
  );
  if (linkRes.success) return true;
  message.error(linkRes.errorMsg || "Couldn't move linked Bid Packages");
  return false;
}

async function moveScopeItems(
  input: Parameters<typeof moveDependentsToMilestone>[0]
): Promise<boolean> {
  const scopeItems = input.scopeListItems.filter((item) =>
    item.design_packages.some(
      (c) =>
        c.design_package?.id === input.designPackageId &&
        c.design_package_milestone?.id === input.milestoneId
    )
  );
  if (!scopeItems.length) return true;
  if (!input.token) {
    message.error("Project auth is not ready. Please try again.");
    return false;
  }
  const feature_instances = scopeItems.map((item) => {
    const row = toModalScopeItem(item);
    return toBulkLinkDesignPackageUpdateInstance(
      row.id,
      row.commitments.map((c) =>
        c.designPackageId === input.designPackageId &&
        c.milestoneId === input.milestoneId
          ? { ...c, milestoneId: input.moveTo }
          : c
      ),
      row.bids
    );
  });
  const scopeRes = await updateDesignScopeInstances(
    {
      feature_type: "DESIGN_SCOPE",
      feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
      feature_instances
    },
    input.token
  );
  if (scopeRes.success) return true;
  message.error(scopeRes.errorMsg || "Couldn't move linked scope items");
  return false;
}

function editableAction<T>(printMode: boolean, action: T): T | undefined {
  if (printMode) return undefined;
  return action;
}

function Lane(props: {
  projectId: string;
  dp: WorkspaceDesignPackage;
  geometry: Geometry | null;
  reportModel: (id: string, model: DependencyModel) => void;
  // Controlled by parent so expand/collapse-all can drive every lane at once.
  expanded: boolean;
  onToggle: () => void;
  // Reloads GET /report-engine/logs/5. Split needs this so the new lane
  // appears; the lane's own `refetch` is the same call (one project-wide log).
  refetchWorkspace: () => void;
  // Every design package's title and number — the Split modal suggests a free
  // "<name> – N" and derives the new package's number from the same free
  // suffix, and rejects a name that already exists (the backend only errors
  // generically on a duplicate design-package name).
  existingTitles: string[];
  existingNumbers: string[];
  // Project-wide Design Scope list (GET /report-engine/logs/10), fetched once
  // by TimelineView so each lane does not re-hit the API.
  scopeListItems: DesignScopeListItem[];
  reloadScopeItems: () => void;
  // Print/PDF: load every lane immediately, hide mutations, and drop chrome
  // (toggle, rename, Manage / Split / View details / Add bid package).
  printMode?: boolean;
  // Card log shows one package, always expanded, so the lane has no collapse
  // control. The lane still loads immediately because it is the only one.
  hideLaneToggle?: boolean;
  // Later bid package dates to scale instead of in the right overflow zone.
  // Owned by TimelineView (a per-user preference); print always draws the
  // zones. The left zone is the same either way.
  fullTimeline?: boolean;
  onFullTimelineChange?: (on: boolean) => void;
}) {
  const {
    projectId,
    dp,
    geometry,
    reportModel,
    expanded,
    onToggle,
    refetchWorkspace,
    existingTitles,
    existingNumbers,
    scopeListItems,
    reloadScopeItems,
    printMode = false,
    hideLaneToggle = false,
    fullTimeline = false,
    onFullTimelineChange
  } = props;
  const [setNode, inView] = useInViewport(printMode || hideLaneToggle);
  // The project log is fetched once; wait until the lane is near the viewport
  // before binding this DP's row so off-screen cards stay cheap. Collapsed
  // lanes still resolve (inView is independent of expanded) so reportModel and
  // the manage-bid-packages modal have data.
  const { model, refetch } = useDesignPackageDetail(inView ? dp.id : null);

  // The manage modal is per-lane so re-linking a bid package re-pulls only this
  // DP's detail (via refetch) on success.
  const [manageOpen, setManageOpen] = useState(false);
  const [addPlacement, setAddPlacement] =
    useState<AddMilestonePlacement | null>(null);
  const [addingMs, setAddingMs] = useState(false);

  const [scopeCtx, setScopeCtx] = useState<ScopeContext | null>(null);

  // Milestones are scoped to THIS design package; a bid package's scope can
  // come from any DP.
  const milestoneScopeItems = useMemo(
    () => groupScopePreviewsByMilestone(scopeListItems, dp.id),
    [scopeListItems, dp.id]
  );
  const bidScopeItems = useMemo(
    () => groupScopePreviewsByBid(scopeListItems),
    [scopeListItems]
  );

  const {
    gqlClientForProject,
    projectToken,
    tokenRetrievalState,
    isProjectArchived
  } = useContext(ProjectContext);
  // Archived projects keep the interactive (non-print) layout but drop every
  // edit affordance.
  const readOnly = printMode || !!isProjectArchived;
  const [renameFeature] = useCIQMutation(UPDATE_FEATURE_INSTANCE, {
    client: gqlClientForProject
  });
  // Renaming only ever touches the title (the viz keeps `number` untouched),
  // so `set` is a single-key patch of feature_instance_set_input.
  const handleRename = useCallback(
    async (bpId: string, title: string) => {
      const res = await renameFeature({
        variables: { featureId: bpId, set: { title } }
      });
      if (res.success) {
        message.success("Bid Package renamed");
        refetch();
      } else {
        message.error("Couldn't rename Bid Package");
      }
    },
    [renameFeature, refetch]
  );

  // The design package's own title, renamed inline from the lane header (the DP
  // name isn't in the viz, so it can't ride the BP rename path). Same mutation,
  // keyed by dp.id; refetch the WORKSPACE (not this lane's detail) since the
  // header reads dp.title from the workspace list. Trimmed + no-op guarded so an
  // empty or unchanged edit doesn't fire a write.
  const [renamingDp, setRenamingDp] = useState(false);
  const [dpNumberDraft, setDpNumberDraft] = useState(dp.number);
  const [dpDraft, setDpDraft] = useState(dp.title);
  const dpRenameCancelled = useRef(false);
  const dpInputRef = useRef<InputRef>(null);
  const dpRenameFieldsRef = useRef<HTMLSpanElement>(null);
  const dpRenameTimer = useRef<number | null>(null);
  // Focus the inline input once it mounts (avoids the autoFocus prop, which the
  // a11y lint flags — a ref focus after the render is the accessible equivalent).
  useEffect(() => {
    if (renamingDp) dpInputRef.current?.focus();
  }, [renamingDp]);
  const handleRenameDesignPackage = useCallback(
    async (rawNumber: string, rawTitle: string) => {
      const title = rawTitle.trim();
      const number = rawNumber.trim();
      if (!title) return;
      const set: { title?: string; number?: string } = {};
      if (title !== dp.title) set.title = title;
      if (number && number !== dp.number) set.number = number;
      if (!set.title && !set.number) return;
      const res = await renameFeature({
        variables: { featureId: dp.id, set }
      });
      if (res.success) {
        message.success("Design package renamed");
        refetchWorkspace();
      } else {
        message.error("Couldn't rename design package");
      }
    },
    [renameFeature, dp.id, dp.title, dp.number, refetchWorkspace]
  );
  // Blur fires as focus moves from the title field to the number field. Wait
  // until the next turn so a move inside the pair does not close the editor.
  const finishDpRename = () => {
    const number = dpNumberDraft;
    const title = dpDraft;
    if (dpRenameTimer.current != null) {
      window.clearTimeout(dpRenameTimer.current);
    }
    dpRenameTimer.current = window.setTimeout(() => {
      dpRenameTimer.current = null;
      if (dpRenameCancelled.current) {
        dpRenameCancelled.current = false;
        setRenamingDp(false);
        return;
      }
      if (dpRenameFieldsRef.current?.contains(document.activeElement)) return;
      setRenamingDp(false);
      handleRenameDesignPackage(number, title);
    }, 0);
  };

  const [applyLinking] = useCIQMutation(MUTATION_APPLY_LINKING_CHANGES, {
    client: gqlClientForProject
  });

  const [applyWorkflowChange] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });
  // Drag-to-reschedule a DESIGN milestone: the viz hands back the day the
  // diamond was dropped on (already snapped + clamped so milestones can't
  // cross); write it as the milestone's committed_date — the same call the
  // date-block editor makes. Refetch both scopes: this lane's detail redraws
  // the viz, and the workspace list feeds the card sort order / header dates.
  const handleMilestoneReschedule = useCallback(
    async (milestoneId: string, isoDate: string) => {
      const workflowInstanceId = model?.designPackage.workflowInstanceId;
      if (!workflowInstanceId) {
        message.error("This design package has no workflow to reschedule");
        return;
      }
      const res = await applyWorkflowChange({
        variables: {
          input: [
            {
              feature_instance_id: dp.id,
              workflow_instance_id: workflowInstanceId,
              milestones: [
                {
                  id: milestoneId,
                  committed_date: DateUtils.formatDateWithLunchTime(isoDate)
                }
              ]
            }
          ],
          metadata: { request_source: "PLANNER" }
        }
      });
      if (res.success) {
        message.success("Milestone rescheduled");
        refetch();
        refetchWorkspace();
      } else {
        message.error("Couldn't reschedule the milestone");
      }
    },
    [applyWorkflowChange, model, dp.id, refetch, refetchWorkspace]
  );

  const handleMilestoneActualDate = useCallback(
    async (milestoneId: string, isoDate: string | null) => {
      const workflowInstanceId = model?.designPackage.workflowInstanceId;
      if (!workflowInstanceId) {
        message.error("This design package has no workflow to reschedule");
        return;
      }
      const res = await applyWorkflowChange({
        variables: {
          input: [
            {
              feature_instance_id: dp.id,
              workflow_instance_id: workflowInstanceId,
              milestones: [
                {
                  id: milestoneId,
                  actual_date: isoDate
                    ? DateUtils.formatDateWithLunchTime(isoDate)
                    : null
                }
              ]
            }
          ],
          metadata: { request_source: "PLANNER" }
        }
      });
      if (res.success) {
        message.success(isoDate ? "Actual date saved" : "Actual date cleared");
        refetch();
        refetchWorkspace();
      } else {
        message.error("Couldn't save the actual date");
      }
    },
    [applyWorkflowChange, model, dp.id, refetch, refetchWorkspace]
  );

  const [updateDpMilestones] = useCIQMutation(
    UPDATE_DESIGN_PACKAGE_MILESTONES,
    { client: gqlClientForProject }
  );
  const handleRenameMilestone = useCallback(
    async (milestoneId: string, nextName: string) => {
      const name = nextName.trim();
      if (!name || !model) return;
      const milestones = model.designPackage.milestones;
      const current = milestones.find((m) => m.id === milestoneId);
      if (!current || current.name === name) return;
      const taken = milestones.some(
        (m) =>
          m.id !== milestoneId &&
          Boolean(m.name) &&
          m.name.trim().toLowerCase() === name.toLowerCase()
      );
      if (taken) {
        message.error("A milestone with that name already exists");
        return;
      }
      const payload = toDesignPackageMilestoneUpdate(milestones, {
        rename: { id: milestoneId, name }
      });
      const res = await updateDpMilestones({
        variables: {
          feature_instance_id: dp.id,
          ...payload
        }
      });
      if (res.success) {
        message.success("Milestone renamed");
        refetch();
      } else {
        message.error("Couldn't rename milestone");
      }
    },
    [updateDpMilestones, model, dp.id, refetch]
  );
  const handleDeleteMilestone = useCallback(
    async (milestoneId: string, options?: { moveToMilestoneId?: string }) => {
      if (!model) return;
      const named = model.designPackage.milestones.filter((m) =>
        Boolean(m.name)
      );
      if (named.length <= 2) {
        message.error("A design package needs at least two milestones");
        return;
      }
      const moveTo = options?.moveToMilestoneId;
      if (moveTo && moveTo !== milestoneId) {
        const moved = await moveDependentsToMilestone({
          moveTo,
          milestoneId,
          named,
          bidPackages: model.bidPackages,
          scopeListItems,
          designPackageId: dp.id,
          token: projectToken || tokenRetrievalState?.token || null,
          applyLinking
        });
        if (!moved) return;
      }
      const payload = toDesignPackageMilestoneUpdate(
        model.designPackage.milestones,
        { deleteId: milestoneId }
      );
      const res = await updateDpMilestones({
        variables: {
          feature_instance_id: dp.id,
          ...payload
        }
      });
      if (res.success) {
        message.success("Milestone deleted");
        refetch();
        refetchWorkspace();
        reloadScopeItems();
      } else {
        message.error("Couldn't delete milestone");
      }
    },
    [
      updateDpMilestones,
      applyLinking,
      model,
      dp.id,
      refetch,
      refetchWorkspace,
      reloadScopeItems,
      scopeListItems,
      projectToken,
      tokenRetrievalState
    ]
  );

  const handleAddMilestone = useCallback(
    async (values: { name: string; isoDate: string }) => {
      if (!model || !addPlacement) return;
      const taken = model.designPackage.milestones.some(
        (m) =>
          Boolean(m.name) &&
          m.name.trim().toLowerCase() === values.name.toLowerCase()
      );
      if (taken) {
        message.error("A milestone with that name already exists");
        return;
      }
      setAddingMs(true);
      const payload = toDesignPackageMilestoneUpdate(
        model.designPackage.milestones,
        {
          insert: {
            name: values.name,
            prevId: addPlacement.prevId,
            nextId: addPlacement.nextId,
            date: values.isoDate
          }
        }
      );
      const res = await updateDpMilestones({
        variables: {
          feature_instance_id: dp.id,
          ...payload
        }
      });
      setAddingMs(false);
      if (!res.success) {
        message.error("Couldn't add milestone");
        return;
      }
      setAddPlacement(null);
      message.success("Milestone added");
      refetch();
      refetchWorkspace();
    },
    [model, addPlacement, updateDpMilestones, dp.id, refetch, refetchWorkspace]
  );

  // Same derivation manage-bid-packages uses: unnamed milestones are internal
  // placeholders (never a valid link target), and the last *named* one is the
  // finish-to-start target (link_type 0); everything before it is link_type 4.
  const namedMilestones = (model?.designPackage.milestones ?? []).filter((m) =>
    Boolean(m.name)
  );
  const lastMilestone = namedMilestones.length
    ? namedMilestones[namedMilestones.length - 1]
    : null;
  const lastMilestoneId = lastMilestone?.id ?? null;
  // A re-link to a different milestone on the same DP↔BP pair is a single
  // `inserts` entry — the backend upserts by (source, target), so no explicit
  // delete is needed. Mirrors manage-bid-packages' `changeMilestone`.
  const commitLink = useCallback(
    async (bpId: string, milestoneId: string) =>
      applyLinking(
        {
          variables: {
            input: {
              inserts: [
                {
                  source_instance_id: dp.id,
                  source_instance_milestone_id: milestoneId,
                  target_instance_id: bpId,
                  link_type: milestoneId === lastMilestoneId ? 0 : 4,
                  coupling_type: 2
                }
              ],
              deletes: [],
              updates: []
            },
            metadata: { request_source: "PLANNER" }
          }
        },
        { showError: true }
      ),
    [applyLinking, dp.id, lastMilestoneId]
  );

  const [relink, setRelink] = useState<{
    bpId: string;
    milestoneId: string;
    caution?: string;
    phase: "confirm" | "progress" | "error";
    work: "link" | "refresh";
    errorMessage?: string;
  } | null>(null);
  const relinkRun = useRef(0);
  const relinkHold = useRef(relink);
  if (relink) relinkHold.current = relink;
  const shownRelink = relink ?? relinkHold.current;

  const handleLinkChange = useCallback(
    (bpId: string, milestoneId: string, info?: { caution?: string }) => {
      const currentId = model?.bidPackages.find((row) => row.id === bpId)
        ?.linkedDpMilestone?.id;
      if (currentId === milestoneId) return;
      setRelink((current) =>
        current?.phase === "progress"
          ? current
          : {
              bpId,
              milestoneId,
              caution: info?.caution,
              phase: "confirm",
              work: "link"
            }
      );
    },
    [model]
  );

  const confirmRelink = useCallback(async () => {
    if (!relink || relink.phase === "progress") return;
    const run = relinkRun.current + 1;
    relinkRun.current = run;
    const request = relink;
    const failLink = (errorMessage?: string) => {
      if (run !== relinkRun.current) return;
      setRelink({
        ...request,
        phase: "error",
        work: "link",
        errorMessage:
          errorMessage ||
          "The milestone link was not changed. Please try again."
      });
      message.error("Couldn't update the link");
    };
    setRelink({ ...request, phase: "progress", work: "link" });
    let res: { success?: boolean; errorMsg?: string };
    try {
      res = await commitLink(request.bpId, request.milestoneId);
    } catch {
      failLink();
      return;
    }
    if (run !== relinkRun.current) return;
    if (!res.success) {
      failLink(res.errorMsg);
      return;
    }
    setRelink({ ...request, phase: "progress", work: "refresh" });
    try {
      await Promise.resolve(refetch());
    } catch {
      if (run !== relinkRun.current) return;
      setRelink({
        ...request,
        phase: "error",
        work: "refresh",
        errorMessage:
          "The link was saved, but the timeline could not be refreshed."
      });
      message.error("Couldn't refresh the timeline");
      return;
    }
    if (run !== relinkRun.current) return;
    setRelink(null);
    const previous = model?.bidPackages.find(
      (row) => row.id === request.bpId
    )?.linkedDpMilestone;
    const nextName = namedMilestones.find(
      (milestone) => milestone.id === request.milestoneId
    )?.name;
    if (previous && previous.id !== request.milestoneId && nextName) {
      message.success({
        key: `planner-relink-${request.bpId}`,
        content: (
          <span className="inline-flex items-center gap-2">
            <span>Re-linked to {nextName}</span>
            {request.caution ? (
              <span className="text-ciq-red-800">· {request.caution}</span>
            ) : null}
          </span>
        )
      });
    } else {
      message.success("Bid Package linked");
    }
  }, [relink, commitLink, model, namedMilestones, refetch]);

  const relinkBid = model?.bidPackages.find(
    (row) => row.id === shownRelink?.bpId
  );
  const relinkFromMilestone = model?.designPackage.milestones.find(
    (milestone) => milestone.id === relinkBid?.linkedDpMilestone?.id
  );
  const relinkToMilestone = model?.designPackage.milestones.find(
    (milestone) => milestone.id === shownRelink?.milestoneId
  );
  const relinkBidLabel =
    bidPackageLabel(relinkBid?.number, relinkBid?.name) || "this bid package";

  const handleSetRequiredOnSite = useCallback(
    async (bidPackageId: string, isoDate: string | null) => {
      const token = projectToken || tokenRetrievalState?.token;
      if (!token) {
        message.error("Project auth is not ready. Please try again.");
        return;
      }
      const res = await linkBidPackages(
        {
          bid_package_ids: [bidPackageId],
          planning_only_activity_date: isoDate
        },
        token
      );
      if (res.success) {
        refetch();
      } else {
        message.error(res.errorMsg || "Couldn't save the on-site date");
      }
    },
    [projectToken, tokenRetrievalState, refetch]
  );

  const [splitDesignPackage] = useCIQMutation(MUTATION_SPLIT_DESIGN_PACKAGE, {
    client: gqlClientForProject
  });
  const [splitOpen, setSplitOpen] = useState(false);
  // Status and risk are not on the planner's log row, but they ride along on
  // the project-wide bid-package list the Manage modal already queries — so the
  // Split table joins that list by id rather than adding a round trip. Fetched
  // only once the modal opens; Apollo serves every other lane from cache.
  const { data: bpListData } = useCIQQuery<{
    feature_instance: Array<{
      id: string;
      number: string | null;
      title: string | null;
      feature_workflow_instance: {
        milestone_state: string | null;
        risk_level: string | null;
      } | null;
    }>;
  }>(BID_PACKAGE_LIST_QUERY_MIN, {
    client: gqlClientForProject,
    skip: !splitOpen || !gqlClientForProject
  });

  const splitBidPackages = useMemo(() => {
    const byId = new Map(
      (bpListData?.feature_instance ?? []).map((bp) => [bp.id, bp])
    );
    return (model?.bidPackages ?? []).map((bp) => {
      const workflow = byId.get(bp.id)?.feature_workflow_instance;
      return {
        id: bp.id,
        label: bidPackageLabel(bp.number, bp.name) || "(Bid Package)",
        status: workflow?.milestone_state ?? null,
        risk: workflow?.risk_level ?? null,
        linkedMilestone: bp.linkedDpMilestone?.name ?? null
      };
    });
  }, [model?.bidPackages, bpListData]);
  const [splitting, setSplitting] = useState(false);
  const [notificationApi, notificationContext] = notification.useNotification();
  // The design only names the new package, so the original's identity goes back
  // unchanged (it stays renameable inline on the lane header). Description is
  // omitted rather than sent as null so the split can't blank an existing one.
  const handleSplit = useCallback(
    async ({ title, number, movedBpIds }: SplitResult) => {
      setSplitting(true);
      const res = await splitDesignPackage(
        {
          variables: {
            design_package_id: dp.id,
            original_design_package: { title: dp.title, number: dp.number },
            new_design_package: { title, number },
            bid_package_ids: movedBpIds
          }
        },
        { showError: true }
      );
      setSplitting(false);

      // showError above already toasts through ErrorManager; a second
      // message.error here would surface the raw GraphQL text alongside it.
      // The modal stays open so the user can retry without re-entering values.
      if (
        !res.success ||
        !res.data?.split_design_package?.new_design_package_id
      ) {
        return;
      }

      setSplitOpen(false);
      // Same shape as the create-design-package notification: what happened,
      // then a link into the new package. The backend's own message names
      // neither package, so the copy is ours.
      notificationApi.open(
        splitDesignPackageNotification({
          projectId,
          newDesignPackageId:
            res.data.split_design_package.new_design_package_id,
          sourceTitle: dp.title,
          newTitle: title
        })
      );
      refetch();
      refetchWorkspace();
    },
    [
      splitDesignPackage,
      dp.id,
      dp.title,
      dp.number,
      projectId,
      notificationApi,
      refetch,
      refetchWorkspace
    ]
  );

  // Report the loaded model up so the parent can rebuild the shared axis.
  // This is a render-output side effect of loading, so it belongs in an effect,
  // never in render/useMemo.
  useEffect(() => {
    if (model) reportModel(dp.id, model);
  }, [model, dp.id, reportModel]);

  const detailsPath = `/project/${projectId}/design-packages/${dp.id}/details`;

  // Full screen pins the lane over the viewport and puts the whole document in
  // browser full screen. Requesting full screen on the lane itself would hide
  // antd modals, dropdowns and tooltips, which portal to document.body.
  const [fullscreen, setFullscreen] = useState(false);
  const fullscreenRef = useRef(false);
  fullscreenRef.current = fullscreen;
  const enterFullscreen = useCallback(() => {
    setFullscreen(true);
    // The lane is already pinned over the viewport, so a refused request
    // (no user gesture, iframe without allowfullscreen, old Safari without the
    // promise API) still leaves a usable full-window view.
    const root = document.documentElement;
    if (document.fullscreenElement || !root.requestFullscreen) return;
    Promise.resolve(root.requestFullscreen()).catch(() => {});
  }, []);
  const exitFullscreen = useCallback(() => {
    setFullscreen(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }, []);
  useEffect(() => {
    if (!fullscreen) return undefined;
    // The browser exits its own full screen on Escape; follow it. The keydown
    // covers browsers where the full-screen request was refused.
    // It also drops its full screen when the user switches tabs or windows.
    // That is not a request to leave, so the lane stays pinned over the
    // window; Escape or the exit button still leave.
    let leftPage = false;
    let backTimer: number | null = null;
    const onAway = () => {
      if (backTimer != null) window.clearTimeout(backTimer);
      backTimer = null;
      leftPage = true;
    };
    const onBack = () => {
      if (document.hidden) return;
      // The browser reports its exit as the page comes back.
      if (backTimer != null) window.clearTimeout(backTimer);
      backTimer = window.setTimeout(() => {
        leftPage = false;
        backTimer = null;
      }, 1000);
    };
    const onVisibility = () => (document.hidden ? onAway() : onBack());
    const onFullscreenChange = () => {
      if (document.fullscreenElement) return;
      if (document.hidden || leftPage) return;
      setFullscreen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitFullscreen();
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onAway);
    window.addEventListener("focus", onBack);
    return () => {
      if (backTimer != null) window.clearTimeout(backTimer);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onAway);
      window.removeEventListener("focus", onBack);
    };
  }, [fullscreen, exitFullscreen]);
  useEffect(
    () => () => {
      if (fullscreenRef.current && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    },
    []
  );
  const showBody = expanded || fullscreen;
  // The lane element, so a full-screen lane can host the timeline's hover
  // portals (see PlannerPortalContext).
  const [laneEl, setLaneEl] = useState<HTMLDivElement | null>(null);
  const laneRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNode(node);
      setLaneEl(node);
    },
    [setNode]
  );

  // The IntersectionObserver callback ref and testid must stay on the outer div —
  // antd Card does not reliably forward a callback ref, and lazy-load depends on
  // this div being the observed element.
  function laneBody() {
    return (
      <div
        ref={laneRef}
        data-testid={`timeline-lane-${dp.id}`}
        className={`flex h-full min-h-0 flex-1 flex-col ${
          printMode ? "w-max" : ""
        } ${fullscreen ? "fixed inset-0 z-[999] bg-white p-4" : ""}`}
      >
        <div className="relative flex h-full min-h-0 flex-1 flex-col">
          {/* Collapse chevron, title link and rename pencil are three separate
          click targets sharing the Card title slot. */}
          <Card
            className={`flex h-full min-h-0 flex-col !rounded-[10px] !border !border-solid !border-ciq-gray-150 shadow-none [&_.ant-card-head]:relative [&_.ant-card-head]:z-10 [&_.ant-card-head]:!min-h-0 [&_.ant-card-head]:shrink-0 [&_.ant-card-head]:!bg-ciq-neutral-25 [&_.ant-card-head]:!px-5 [&_.ant-card-head]:!py-4 [&_.ant-card-head-wrapper]:!items-center [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col [&_.ant-card-head-title]:!min-w-0 [&_.ant-card-head-title]:!flex-1 ${
              printMode
                ? "w-max overflow-visible [&_.ant-card-body]:overflow-visible"
                : "overflow-hidden [&_.ant-card-body]:overflow-hidden"
            } ${
              showBody
                ? "[&_.ant-card-head]:!border-0 [&_.ant-card-head]:!border-b [&_.ant-card-head]:!border-solid [&_.ant-card-head]:!border-b-ciq-gray-300"
                : "[&_.ant-card-head]:!border-b-0"
            }`}
            title={
              <div className="flex w-full min-w-0 items-center gap-2 font-normal">
                {!printMode && !hideLaneToggle && (
                  <Button
                    type="text"
                    data-testid={`lane-toggle-${dp.id}`}
                    aria-label={`Toggle ${dp.number} ${dp.title}`}
                    className="flex-shrink-0 !text-ciq-primary-900 hover:!bg-transparent focus:!bg-transparent hover:!border-transparent focus:!border-transparent"
                    icon={expanded ? <DownOutlined /> : <RightOutlined />}
                    onClick={onToggle}
                  />
                )}
                <div className="flex min-w-[180px] flex-1 flex-col gap-0.5">
                  {printMode ? (
                    <Typography.Text
                      strong
                      data-testid={`lane-dp-title-${dp.id}`}
                    >
                      {dp.number} - {dp.title}
                    </Typography.Text>
                  ) : renamingDp ? (
                    <span
                      ref={dpRenameFieldsRef}
                      className="flex items-center gap-2"
                    >
                      <Input
                        size="small"
                        value={dpNumberDraft}
                        data-testid={`lane-dp-number-input-${dp.id}`}
                        className="max-w-[100px]"
                        onChange={(e) => setDpNumberDraft(e.target.value)}
                        onPressEnter={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            dpRenameCancelled.current = true;
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={finishDpRename}
                      />
                      <Input
                        ref={dpInputRef}
                        size="small"
                        value={dpDraft}
                        data-testid={`lane-dp-title-input-${dp.id}`}
                        className="max-w-[240px]"
                        onChange={(e) => setDpDraft(e.target.value)}
                        onPressEnter={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            dpRenameCancelled.current = true;
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={finishDpRename}
                      />
                    </span>
                  ) : (
                    <span className="flex min-w-0 max-w-full items-center gap-2.5 self-start">
                      <Link
                        to={detailsPath}
                        rel="noopener noreferrer"
                        data-testid={`lane-dp-title-${dp.id}`}
                        title="Open design package details"
                        className="min-w-0 truncate text-[17px] font-bold leading-6 tracking-[-0.01em] text-black hover:text-ciq-blue-600 hover:underline hover:underline-offset-2"
                      >
                        {[dp.number, dp.title].filter(Boolean).join(" ")}
                      </Link>
                      {!isProjectArchived && (
                        <button
                          type="button"
                          data-testid={`lane-dp-rename-${dp.id}`}
                          aria-label="Rename design package"
                          title="Rename"
                          className="flex shrink-0 cursor-pointer items-center border-none bg-transparent p-0 text-base text-ciq-planner-accent"
                          onClick={() => {
                            setDpNumberDraft(dp.number);
                            setDpDraft(dp.title);
                            setRenamingDp(true);
                          }}
                        >
                          <EditOutlined />
                        </button>
                      )}
                    </span>
                  )}
                  {!printMode && dp.description ? (
                    <div
                      data-testid={`lane-dp-description-${dp.id}`}
                      className="mt-0.5 truncate text-sm font-normal leading-5 text-ciq-planner-ink4"
                    >
                      {dp.description}
                    </div>
                  ) : null}
                </div>
              </div>
            }
            extra={editableAction(
              printMode,
              <Space size={14} align="center">
                {onFullTimelineChange ? (
                  <Tooltip title="View the full timeline in scale. Scroll right or left of the design milestones to explore.">
                    <label className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-[13.5px] font-medium text-ciq-planner-ink">
                      Full timeline
                      <Switch
                        size="small"
                        data-testid={`lane-full-timeline-${dp.id}`}
                        aria-label="Full timeline"
                        checked={fullTimeline}
                        onChange={onFullTimelineChange}
                      />
                    </label>
                  </Tooltip>
                ) : null}
                <Button
                  icon={<LinkOutlined />}
                  disabled={!model || !!isProjectArchived}
                  data-testid={`lane-manage-${dp.id}`}
                  className="!h-8 !rounded-[7px] !border-ciq-planner-action !px-3.5 !text-[13.5px] !font-medium !text-ciq-planner-accent"
                  onClick={() => setManageOpen(true)}
                >
                  Manage Bid Packages
                </Button>
                <Button
                  icon={
                    <BranchesOutlined className="!text-ciq-planner-accent" />
                  }
                  data-testid={`lane-split-${dp.id}`}
                  disabled={!!isProjectArchived}
                  className="!h-8 !rounded-[7px] !border-ciq-planner-action !px-3.5 !text-[13.5px] !font-medium !text-ciq-planner-ink"
                  onClick={() => setSplitOpen(true)}
                >
                  Split
                </Button>
                <Button
                  icon={
                    fullscreen ? (
                      <FullscreenExitOutlined className="!text-base !text-ciq-planner-accent" />
                    ) : (
                      <FullscreenOutlined className="!text-base !text-ciq-planner-accent" />
                    )
                  }
                  data-testid={`lane-fullscreen-${dp.id}`}
                  className="!flex !h-8 !w-8 !min-w-0 !items-center !justify-center !rounded-[7px] !border-ciq-planner-action !p-0"
                  aria-label={fullscreen ? "Exit full screen" : "Full screen"}
                  onClick={fullscreen ? exitFullscreen : enterFullscreen}
                />
              </Space>
            )}
            // Always zero so the SVG timeline sits flush against the card edges.
            styles={{ body: { padding: 0 } }}
          >
            {showBody && geometry && (
              <PlannerPortalContext.Provider value={fullscreen ? laneEl : null}>
                <DependencyTimeline
                  geometry={geometry}
                  config={DEFAULT_RENDER_CONFIG}
                  milestoneScopeItems={milestoneScopeItems}
                  bidScopeItems={bidScopeItems}
                  crosshair={!printMode}
                  onRenameBidPackage={editableAction(readOnly, handleRename)}
                  onRenameMilestone={editableAction(
                    readOnly,
                    handleRenameMilestone
                  )}
                  onDeleteMilestone={editableAction(
                    readOnly,
                    handleDeleteMilestone
                  )}
                  onAddMilestone={editableAction(readOnly, setAddPlacement)}
                  onLinkChange={editableAction(readOnly, handleLinkChange)}
                  onMilestoneReschedule={editableAction(
                    readOnly,
                    handleMilestoneReschedule
                  )}
                  onMilestoneActualDate={editableAction(
                    readOnly,
                    handleMilestoneActualDate
                  )}
                  onMilestoneScopeOpen={editableAction(printMode, (m) => {
                    const milestone = model?.designPackage.milestones.find(
                      (row) => row.id === m.milestoneId
                    );
                    const linkedBidCount =
                      model?.bidPackages.filter(
                        (row) => row.linkedDpMilestone?.id === m.milestoneId
                      ).length ?? 0;
                    const designPackageTitle =
                      dp.title || dp.number || "Design package";
                    setScopeCtx({
                      kind: "milestone",
                      designPackageId: dp.id,
                      milestoneId: m.milestoneId,
                      label: `${designPackageTitle} · ${m.label}`,
                      designPackageTitle,
                      milestoneName: m.label,
                      committedDate:
                        milestone?.committedDate ||
                        milestone?.effectiveDate ||
                        null,
                      projectedDate: milestone?.projectedDate || null,
                      actualDate: milestone?.actualDate || null,
                      linkedBidCount
                    });
                  })}
                  onBidScopeOpen={editableAction(printMode, (b) =>
                    setScopeCtx({
                      kind: "bid",
                      bidPackageId: b.bidPackageId,
                      label: b.label,
                      designPackageName: dp.title || dp.number || "",
                      designPackageId: dp.id,
                      milestoneId: model?.bidPackages.find(
                        (row) => row.id === b.bidPackageId
                      )?.linkedDpMilestone?.id,
                      milestoneName: b.milestoneName
                    })
                  )}
                  plannerLayout
                  printMode={printMode}
                  fullTimeline={!printMode && fullTimeline}
                  projectId={projectId}
                  designPackageName={dp.title || dp.number || ""}
                  onSetRequiredOnSite={editableAction(
                    readOnly,
                    handleSetRequiredOnSite
                  )}
                  onManageBidPackages={editableAction(readOnly, () =>
                    setManageOpen(true)
                  )}
                  onCreatePlan={editableAction(
                    readOnly,
                    (bidPackageId: string) =>
                      window.open(
                        bidPackageDetailsPath(projectId, bidPackageId),
                        "_blank",
                        "noopener,noreferrer"
                      )
                  )}
                />
              </PlannerPortalContext.Provider>
            )}
          </Card>
        </div>

        <ManageBidPackagesModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          designPackageId={dp.id}
          designPackageLabel={`${dp.number} ${dp.title}`}
          model={model}
          onChanged={refetch}
        />

        {scopeCtx && (
          <ScopeListModal
            open
            projectId={projectId}
            context={scopeCtx}
            onClose={() => setScopeCtx(null)}
            onChanged={reloadScopeItems}
          />
        )}

        {notificationContext}

        <SplitDesignPackageModal
          open={splitOpen}
          onClose={() => setSplitOpen(false)}
          sourceTitle={dp.title}
          sourceNumber={dp.number}
          milestoneCount={namedMilestones.length}
          existingTitles={existingTitles}
          existingNumbers={existingNumbers}
          bidPackages={splitBidPackages}
          onConfirm={handleSplit}
          confirmLoading={splitting}
        />

        <AddDesignPackageMilestoneModal
          open={Boolean(addPlacement)}
          placement={addPlacement}
          existingNames={namedMilestones.map((m) => m.name)}
          confirmLoading={addingMs}
          onClose={() => setAddPlacement(null)}
          onConfirm={handleAddMilestone}
        />

        <RelinkMilestoneModal
          open={Boolean(relink && relinkToMilestone)}
          phase={shownRelink?.phase ?? "confirm"}
          work={shownRelink?.work ?? "link"}
          errorMessage={shownRelink?.errorMessage}
          bidLabel={relinkBidLabel}
          from={{
            name: relinkBid?.linkedDpMilestone?.name || "Not linked",
            actualDate: relinkFromMilestone?.actualDate ?? null,
            committedDate: relinkFromMilestone?.committedDate ?? null
          }}
          to={{
            name: relinkToMilestone?.name || "",
            actualDate: relinkToMilestone?.actualDate ?? null,
            committedDate: relinkToMilestone?.committedDate ?? null
          }}
          onCancel={() => {
            if (shownRelink?.phase === "progress") return;
            relinkRun.current += 1;
            setRelink(null);
          }}
          onConfirm={() => {
            confirmRelink();
          }}
        />
      </div>
    );
  }
  return laneBody();
}

export default function TimelineView(props: {
  projectId: string;
  model: WorkspaceModel;
  // Collapse state is owned by the parent. The card log passes an empty set
  // and hideLaneToggle so the single visible package stays expanded.
  collapsedIds: Set<string>;
  onToggleLane: (id: string) => void;
  // Reloads the project-wide design-package log, owned by PlannerCardView —
  // threaded down so Split can bring the new lane into view once create succeeds.
  refetchWorkspace: () => void;
  // Titles of every design package, including ones not rendered in this view.
  // Split uses them to reject a duplicate name. Defaults to the rendered model.
  existingTitles?: string[];
  // Read-only print/PDF: expand every lane, skip viewport lazy-load, hide
  // mutations and lane chrome so the cards can be printed as-is.
  printMode?: boolean;
  hideLaneToggle?: boolean;
}) {
  const { projectToken, tokenRetrievalState } = useContext(ProjectContext);
  const { items: scopeListItems, reload: reloadScopeItems } =
    useDesignScopeList(projectToken || tokenRetrievalState?.token);
  const { designPackages } = props.model;
  const {
    collapsedIds,
    onToggleLane,
    refetchWorkspace,
    printMode,
    hideLaneToggle
  } = props;
  // One setting for every lane and design package until a reload. Print keeps
  // the zones.
  const [fullTimelinePref, changeFullTimeline] =
    useTimelineViewPref("fullTimeline");
  const fullTimeline = !printMode && fullTimelinePref;
  // Every package's title and number, so each lane's Split modal can default to
  // a free "<name> – N" and derive the new package's number from it. Titles may
  // be supplied by the caller to cover packages outside the rendered model.
  const existingTitles =
    props.existingTitles ?? designPackages.map((d) => d.title);
  const existingNumbers = designPackages.map((d) => d.number);

  // Loaded lane models keyed by design-package id. The shared axis is rebuilt
  // from whatever has loaded so far, so the visible lanes always agree on x.
  const [models, setModels] = useState<Record<string, DependencyModel>>({});

  // The identity guard keeps an unchanged model from triggering a re-render,
  // which would otherwise loop the lane's reporting effect.
  const reportModel = useCallback((id: string, model: DependencyModel) => {
    setModels((prev) => (prev[id] === model ? prev : { ...prev, [id]: model }));
  }, []);

  if (designPackages.length === 0) {
    return (
      <Empty
        data-testid="timeline-empty"
        description="No Design Packages in this project yet"
      />
    );
  }

  return (
    // flex-col + gap-3 gives card-level spacing; the parent (PlannerCardView)
    // owns the scroll + background.
    <div
      data-testid="planning-timeline"
      className={`flex h-full min-h-0 flex-col gap-6 p-4 ${
        printMode ? "w-max overflow-visible" : "overflow-hidden"
      }`}
    >
      {designPackages.map((dp) => {
        const m = models[dp.id] ?? null;
        // Each design package fits its own milestone range, so its timeline
        // fills the lane and one DP's dates never rescale another's. A shared
        // project-wide axis (for cross-DP comparison) will come as its own view.
        const geometry = m
          ? computeGeometry(m, DEFAULT_RENDER_CONFIG, undefined, {
              designSpanOnly: true,
              sideMargin: PLANNER_SIDE_MARGIN
            })
          : null;
        return (
          <Lane
            key={dp.id}
            projectId={props.projectId}
            dp={dp}
            geometry={geometry}
            reportModel={reportModel}
            expanded={!collapsedIds.has(dp.id)}
            onToggle={() => onToggleLane(dp.id)}
            refetchWorkspace={refetchWorkspace}
            existingTitles={existingTitles}
            existingNumbers={existingNumbers}
            scopeListItems={scopeListItems}
            reloadScopeItems={reloadScopeItems}
            printMode={printMode}
            hideLaneToggle={hideLaneToggle}
            fullTimeline={fullTimeline}
            onFullTimelineChange={printMode ? undefined : changeFullTimeline}
          />
        );
      })}
    </div>
  );
}
